import update, { Spec } from 'immutability-helper'
import { StoreActionMap } from '@/store/types'
import { FavoriteType } from '@/modules/Favorites/apiTypes/GetFavorites'
import { UserCard } from '@/dataV3/Search/UserCard'

export type FavoritesCategoryState = {
  totalCount: number
  favorites: UserCard[]
  nextPage: string | null
}

export interface FavoritesReducer {
  [FavoriteType.sent]: FavoritesCategoryState
  [FavoriteType.received]: FavoritesCategoryState
  [FavoriteType.mutual]: FavoritesCategoryState
}

export const initialFavoritesReducerState: FavoritesReducer = {
  [FavoriteType.sent]: {
    favorites: [],
    totalCount: 0,
    nextPage: null,
  },
  [FavoriteType.received]: {
    favorites: [],
    totalCount: 0,
    nextPage: null,
  },
  [FavoriteType.mutual]: {
    favorites: [],
    totalCount: 0,
    nextPage: null,
  },
}

export enum FavoritesTypes {
  Restore = 'Favorites/RESTORE',
  UpdateFavorites = 'Favorites/UPDATE_FAVORITE',
  DeleteFavorite = 'Favorites/DELETE_FAVORITE',
  Logout = 'Favorites/LOGOUT',
}

type FavoritesPayload = {
  [FavoritesTypes.Restore]: FavoritesReducer
  [FavoritesTypes.UpdateFavorites]: FavoritesCategoryState & { mode: FavoriteType; isFirstPage: boolean }
  [FavoritesTypes.DeleteFavorite]: { favoriteId: string }
  [FavoritesTypes.Logout]: null
}

export type FavoritesActions = StoreActionMap<FavoritesPayload>[keyof StoreActionMap<FavoritesPayload>]

const favoritesReducer = (curState: FavoritesReducer, action: FavoritesActions): FavoritesReducer => {
  switch (action.type) {
    case FavoritesTypes.Restore:
      return {
        ...action.payload,
      }
    case FavoritesTypes.UpdateFavorites: {
      const { favorites, totalCount, mode, nextPage, isFirstPage } = action.payload

      const stateUpdate: Spec<FavoritesReducer> = {
        [mode]: {
          totalCount: { $set: totalCount },
          nextPage: { $set: nextPage },
          // Reset or push new discussions
          favorites: isFirstPage ? { $set: favorites } : { $push: favorites },
        },
      }

      return update(curState, stateUpdate)
    }
    case FavoritesTypes.DeleteFavorite: {
      const { favoriteId } = action.payload

      // First, remove from "sent"
      const mineIndex = curState[FavoriteType.sent].favorites.findIndex((fav) => {
        if (fav.id === favoriteId) {
          return true
        }
        return false
      })
      const updatedState: Spec<FavoritesReducer> = {}
      if (mineIndex >= 0) {
        // Favorite exists
        updatedState[FavoriteType.sent] = {
          favorites: { $splice: [[mineIndex, 1]] },
          totalCount: {
            $apply: function (x) {
              return x - 1
            },
          },
        }
      } else {
        // Favorite does not exists, do nothing
      }

      // Then, remove in "mutual" if exists
      const bothIndex = curState[FavoriteType.mutual].favorites.findIndex((fav) => {
        if (fav.id === favoriteId) {
          return true
        }
        return false
      })
      if (bothIndex >= 0) {
        // Favorite exists
        updatedState[FavoriteType.mutual] = {
          favorites: { $splice: [[bothIndex, 1]] },
          totalCount: {
            $apply: function (x) {
              return x - 1
            },
          },
        }
      } else {
        // Favorite does not exist, do nothing
      }

      return update(curState, updatedState)
    }
    case FavoritesTypes.Logout:
      return initialFavoritesReducerState
    default:
      return curState
  }
}

export default favoritesReducer
